import { supabase } from '../lib/supabase';

export interface GameResult {
  user_id: string;
  game_type: 'math_challenge' | 'sum_grid' | 'multiplication_grid' | 'combined_grid' | 'mental_math_speed';
  score: number;
  completion_time_seconds: number;
  hints_used: number;
  accuracy?: number;
  completed: boolean;
  session_data?: Record<string, any>;
}

export interface LeaderboardEntry {
  user_id: string;
  user_name: string;
  game_type: string;
  score: number;
  completion_time: number;
  accuracy: number;
  hints_used: number;
  completed_at: string;
  rank: number;
}

export interface DailyLeaderboardEntry {
  user_id: string;
  user_name: string;
  game_type: string;
  completion_time_seconds: number;
  hints_used: number;
  accuracy: number;
  completed_at: string;
  daily_rank: number;
  challenge_date: string;
  country?: string;
}

// Save a game result to the database
export const saveGameResult = async (result: GameResult): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('game_sessions')
      .insert({
        user_id: result.user_id,
        game_type: result.game_type,
        score: result.score,
        completion_time_seconds: result.completion_time_seconds,
        hints_used: result.hints_used,
        accuracy: result.accuracy,
        completed: result.completed,
        completed_at: new Date().toISOString(),
        session_data: result.session_data
      });

    if (error) {
      console.error('Error saving game result:', error);
      return false;
    }

    // Log activity for streak tracking (luxury growth blueprint engagement)
    try {
      await supabase.rpc('log_daily_activity', {
        p_user_id: result.user_id,
        p_game_type: result.game_type,
        p_score: result.score
      });
    } catch (streakError) {
      console.warn('Streak tracking failed:', streakError);
      // Don't fail the main save if streak tracking fails
    }

    return true;
  } catch (error) {
    console.error('Error saving game result:', error);
    return false;
  }
};

// Get leaderboard for a specific game type
export const getGameLeaderboard = async (
  gameType: string, 
  limit: number = 10
): Promise<LeaderboardEntry[]> => {
  try {
    const { data, error } = await supabase
      .from('game_sessions')
      .select(`
        user_id,
        game_type,
        score,
        completion_time_seconds,
        accuracy,
        hints_used,
        completed_at,
        profiles!inner(first_name, last_name, username)
      `)
      .eq('game_type', gameType)
      .eq('completed', true)
      .order('score', { ascending: false })
      .order('completion_time_seconds', { ascending: true })
      .limit(limit);

    if (error) {
      console.error('Error fetching leaderboard:', error);
      return [];
    }

    return (data || []).map((entry, index) => ({
      user_id: entry.user_id,
      user_name: (entry as any).profiles?.username ? `@${(entry as any).profiles.username}` : `${(entry as any).profiles?.first_name || 'Anonymous'} ${(entry as any).profiles?.last_name || 'User'}`.trim(),
      game_type: entry.game_type,
      score: entry.score || 0,
      completion_time: entry.completion_time_seconds || 0,
      accuracy: entry.accuracy || 0,
      hints_used: entry.hints_used || 0,
      completed_at: entry.completed_at,
      rank: index + 1
    }));
  } catch (error) {
    console.error('Error fetching leaderboard:', error);
    return [];
  }
};

// Get combined leaderboard across all games
export const getCombinedLeaderboard = async (limit: number = 20): Promise<LeaderboardEntry[]> => {
  try {
    const { data, error } = await supabase
      .from('game_sessions')
      .select(`
        user_id,
        game_type,
        score,
        completion_time_seconds,
        accuracy,
        hints_used,
        completed_at,
        profiles!inner(first_name, last_name, username)
      `)
      .eq('completed', true)
      .order('score', { ascending: false })
      .order('completion_time_seconds', { ascending: true })
      .limit(limit);

    if (error) {
      console.error('Error fetching combined leaderboard:', error);
      return [];
    }

    return (data || []).map((entry, index) => ({
      user_id: entry.user_id,
      user_name: (entry as any).profiles?.username ? `@${(entry as any).profiles.username}` : `${(entry as any).profiles?.first_name || 'Anonymous'} ${(entry as any).profiles?.last_name || 'User'}`.trim(),
      game_type: entry.game_type,
      score: entry.score || 0,
      completion_time: entry.completion_time_seconds || 0,
      accuracy: entry.accuracy || 0,
      hints_used: entry.hints_used || 0,
      completed_at: entry.completed_at,
      rank: index + 1
    }));
  } catch (error) {
    console.error('Error fetching combined leaderboard:', error);
    return [];
  }
};

// Get user's personal best scores
export const getUserPersonalBests = async (userId: string): Promise<{
  math_challenge?: LeaderboardEntry;
  sum_grid?: LeaderboardEntry;
  multiplication_grid?: LeaderboardEntry;
  combined_grid?: LeaderboardEntry;
}> => {
  try {
    const gameTypes = ['math_challenge', 'sum_grid', 'multiplication_grid', 'combined_grid'];
    const personalBests: any = {};

    for (const gameType of gameTypes) {
      const { data, error } = await supabase
        .from('game_sessions')
        .select(`
          user_id,
          game_type,
          score,
          completion_time_seconds,
          accuracy,
          hints_used,
          completed_at,
          profiles!inner(first_name, last_name, username)
        `)
        .eq('user_id', userId)
        .eq('game_type', gameType)
        .eq('completed', true)
        .order('score', { ascending: false })
        .order('completion_time_seconds', { ascending: true })
        .limit(1);

      if (!error && data && data.length > 0) {
        const entry = data[0];
        personalBests[gameType] = {
          user_id: entry.user_id,
          user_name: (entry as any).profiles?.username ? `@${(entry as any).profiles.username}` : `${(entry as any).profiles?.first_name || 'Anonymous'} ${(entry as any).profiles?.last_name || 'User'}`.trim(),
          game_type: entry.game_type,
          score: entry.score || 0,
          completion_time: entry.completion_time_seconds || 0,
          accuracy: entry.accuracy || 0,
          hints_used: entry.hints_used || 0,
          completed_at: entry.completed_at,
          rank: 0 // Will be calculated when needed
        };
      }
    }

    return personalBests;
  } catch (error) {
    console.error('Error fetching personal bests:', error);
    return {};
  }
};

// Helper function to format game type names
export const formatGameTypeName = (gameType: string): string => {
  switch (gameType) {
    case 'math_challenge':
      return 'Math Challenge';
    case 'sum_grid':
      return 'Sum Grid';
    case 'multiplication_grid':
      return 'Multiplication Grid';
    case 'combined_grid':
      return 'Combined Grid';
    case 'mental_math_speed':
      return 'Mental Math Speed';
    default:
      return gameType;
  }
};

// Helper function to get game type emoji
export const getGameTypeEmoji = (gameType: string): string => {
  switch (gameType) {
    case 'math_challenge':
      return '🧮';
    case 'sum_grid':
      return '➕';
    case 'multiplication_grid':
      return '✖️';
    case 'combined_grid':
      return '🔢';
    case 'mental_math_speed':
      return '🧠';
    default:
      return '🎮';
  }
};

// Save daily challenge result for leaderboard (only for non-practice mode)
export const saveDailyChallengeResult = async (
  userId: string,
  gameType: 'sum_grid' | 'multiplication_grid' | 'combined_grid',
  completionTimeSeconds: number,
  hintsUsed: number = 0,
  accuracy: number = 100
): Promise<boolean> => {
  try {
    const { error } = await supabase.rpc('save_daily_grid_result', {
      p_user_id: userId,
      p_game_type: gameType,
      p_completion_time_seconds: completionTimeSeconds,
      p_hints_used: hintsUsed,
      p_accuracy: accuracy
    });

    if (error) {
      console.error('Error saving daily challenge result:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error saving daily challenge result:', error);
    return false;
  }
};

// Get today's daily challenge leaderboard for a specific game
export const getTodaysDailyLeaderboard = async (
  gameType: 'sum_grid' | 'multiplication_grid' | 'combined_grid',
  limit: number = 25
): Promise<DailyLeaderboardEntry[]> => {
  try {
    // Bot user IDs to exclude from daily challenges
    const BOT_USER_IDS = [
      '00000000-0000-0000-0000-000000000001', // eightythree_pro
      '00000000-0000-0000-0000-000000000002', // support_the_ebook
      '00000000-0000-0000-0000-000000000003'  // labs_community
    ];

    const { data, error } = await supabase
      .from('todays_grid_leaderboard')
      .select('*')
      .eq('game_type', gameType)
      .not('user_id', 'in', `(${BOT_USER_IDS.join(',')})`)
      .order('daily_rank', { ascending: true })
      .limit(limit);

    if (error) {
      console.error('Error fetching today\'s daily leaderboard:', error);
      return [];
    }

    return (data || []).map(entry => ({
      user_id: entry.user_id,
      user_name: entry.username ? (entry.username.startsWith('@') ? entry.username : `@${entry.username}`) : 'Anonymous User',
      game_type: entry.game_type,
      completion_time_seconds: entry.completion_time_seconds,
      hints_used: entry.hints_used,
      accuracy: entry.accuracy,
      completed_at: entry.completed_at,
      daily_rank: entry.daily_rank,
      challenge_date: entry.challenge_date,
      country: entry.country
    }));
  } catch (error) {
    console.error('Error fetching today\'s daily leaderboard:', error);
    return [];
  }
};

// Get all daily challenge leaderboards for today (all games)
export const getAllTodaysDailyLeaderboards = async (
  limit: number = 25
): Promise<{
  sum_grid: DailyLeaderboardEntry[];
  multiplication_grid: DailyLeaderboardEntry[];
  combined_grid: DailyLeaderboardEntry[];
}> => {
  try {
    const [sumGrid, multiplicationGrid, combinedGrid] = await Promise.all([
      getTodaysDailyLeaderboard('sum_grid', limit),
      getTodaysDailyLeaderboard('multiplication_grid', limit),
      getTodaysDailyLeaderboard('combined_grid', limit)
    ]);

    return {
      sum_grid: sumGrid,
      multiplication_grid: multiplicationGrid,
      combined_grid: combinedGrid
    };
  } catch (error) {
    console.error('Error fetching all today\'s daily leaderboards:', error);
    return {
      sum_grid: [],
      multiplication_grid: [],
      combined_grid: []
    };
  }
};

// Get user's position in today's leaderboard for a specific game
export const getUserDailyRank = async (
  userId: string,
  gameType: 'sum_grid' | 'multiplication_grid' | 'combined_grid'
): Promise<DailyLeaderboardEntry | null> => {
  try {
    // Bot user IDs to exclude from daily challenges
    const BOT_USER_IDS = [
      '00000000-0000-0000-0000-000000000001', // eightythree_pro
      '00000000-0000-0000-0000-000000000002', // support_the_ebook
      '00000000-0000-0000-0000-000000000003'  // labs_community
    ];

    // First get the user's actual data
    const { data, error } = await supabase
      .from('todays_grid_leaderboard')
      .select('*')
      .eq('game_type', gameType)
      .eq('user_id', userId)
      .single();

    if (error || !data) {
      return null;
    }

    // Now calculate the real rank excluding bots
    const { data: allEntries, error: rankError } = await supabase
      .from('todays_grid_leaderboard')
      .select('*')
      .eq('game_type', gameType)
      .not('user_id', 'in', `(${BOT_USER_IDS.join(',')})`)
      .order('accuracy', { ascending: false })
      .order('completion_time_seconds', { ascending: true })
      .order('hints_used', { ascending: true });

    let realRank = 1;
    if (!rankError && allEntries) {
      const userIndex = allEntries.findIndex(entry => entry.user_id === userId);
      realRank = userIndex >= 0 ? userIndex + 1 : data.daily_rank;
    }

    return {
      user_id: data.user_id,
      user_name: data.username ? (data.username.startsWith('@') ? data.username : `@${data.username}`) : 'Anonymous User',
      game_type: data.game_type,
      completion_time_seconds: data.completion_time_seconds,
      hints_used: data.hints_used,
      accuracy: data.accuracy,
      completed_at: data.completed_at,
      daily_rank: realRank, // Use the real rank excluding bots
      challenge_date: data.challenge_date,
      country: data.country
    };
  } catch (error) {
    console.error('Error fetching user daily rank:', error);
    return null;
  }
};

// Check if user has already completed today's daily challenge
export const hasCompletedTodaysChallenge = async (
  userId: string,
  gameType: 'sum_grid' | 'multiplication_grid' | 'combined_grid'
): Promise<boolean> => {
  try {
    const { data, error } = await supabase
      .from('daily_grid_results')
      .select('id')
      .eq('user_id', userId)
      .eq('game_type', gameType)
      .eq('challenge_date', new Date().toISOString().split('T')[0])
      .single();

    return !error && !!data;
  } catch (error) {
    console.error('Error checking daily challenge completion:', error);
    return false;
  }
};

// Get time until next daily challenge (midnight EST)
export const getTimeUntilNextChallenge = (): { hours: number; minutes: number; seconds: number } => {
  const now = new Date();
  const est = new Date(now.toLocaleString("en-US", { timeZone: "America/New_York" }));
  
  // Get tomorrow at midnight EST
  const tomorrow = new Date(est);
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(0, 0, 0, 0);
  
  // Convert back to local time for calculation
  const tomorrowUTC = new Date(tomorrow.toLocaleString("en-US", { timeZone: "UTC" }));
  const nowUTC = new Date(now.toLocaleString("en-US", { timeZone: "UTC" }));
  
  const diffMs = tomorrowUTC.getTime() - nowUTC.getTime();
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);
  
  return { hours, minutes, seconds };
};

// Streak tracking functions - Core to luxury growth blueprint

export interface UserStreak {
  current_streak: number;
  longest_streak: number;
  last_activity_date: string | null;
  streak_freeze_count: number;
}

export interface StreakReward {
  id: string;
  reward_type: 'badge' | 'unlock' | 'premium_trial';
  reward_name: string;
  streak_milestone: number;
  earned_at: string;
  claimed: boolean;
}

// Get user's current streak information
export const getUserStreak = async (userId: string): Promise<UserStreak | null> => {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('current_streak, longest_streak, last_activity_date, streak_freeze_count')
      .eq('id', userId)
      .single();

    if (error || !data) {
      return null;
    }

    return {
      current_streak: data.current_streak || 0,
      longest_streak: data.longest_streak || 0,
      last_activity_date: data.last_activity_date,
      streak_freeze_count: data.streak_freeze_count || 0
    };
  } catch (error) {
    console.error('Error fetching user streak:', error);
    return null;
  }
};

// Get user's unclaimed streak rewards
export const getUnclaimedStreakRewards = async (userId: string): Promise<StreakReward[]> => {
  try {
    const { data, error } = await supabase
      .from('streak_rewards')
      .select('*')
      .eq('user_id', userId)
      .eq('claimed', false)
      .order('earned_at', { ascending: false });

    if (error) {
      console.error('Error fetching streak rewards:', error);
      return [];
    }

    return (data || []).map(reward => ({
      id: reward.id,
      reward_type: reward.reward_type,
      reward_name: reward.reward_name,
      streak_milestone: reward.streak_milestone,
      earned_at: reward.earned_at,
      claimed: reward.claimed
    }));
  } catch (error) {
    console.error('Error fetching streak rewards:', error);
    return [];
  }
};

// Claim a streak reward
export const claimStreakReward = async (rewardId: string): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('streak_rewards')
      .update({ claimed: true })
      .eq('id', rewardId);

    if (error) {
      console.error('Error claiming streak reward:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error claiming streak reward:', error);
    return false;
  }
};

// Get streak leaderboard - top current streaks
export const getStreakLeaderboard = async (limit: number = 20): Promise<Array<{
  user_name: string;
  current_streak: number;
  longest_streak: number;
  rank: number;
}>> => {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select(`
        current_streak,
        longest_streak,
        username,
        first_name,
        last_name
      `)
      .gt('current_streak', 0)
      .order('current_streak', { ascending: false })
      .order('longest_streak', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Error fetching streak leaderboard:', error);
      return [];
    }

    return (data || []).map((entry, index) => ({
      user_name: entry.username ? `@${entry.username}` : `${entry.first_name || 'Anonymous'} ${entry.last_name || 'User'}`.trim(),
      current_streak: entry.current_streak || 0,
      longest_streak: entry.longest_streak || 0,
      rank: index + 1
    }));
  } catch (error) {
    console.error('Error fetching streak leaderboard:', error);
    return [];
  }
};